"use client";

import { AppState } from "@/store/store";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import styles from "./announcementBar.module.scss";

/**
 * Minimal client-side wrapper that only manages z-index based on drawer state.
 * This is needed so the announcement bar appears behind the dim background on mobile when the drawer is open.
 * The announcement bar itself is a server component that handles all dismissal logic via Server Actions.
 */
export default function AnnouncementBarClientWrapper({
  children,
  isHomepage = false,
}: {
  children: React.ReactNode;
  isHomepage?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const openMenu = useSelector((state: AppState) => state.drawerMenuOpenSlice.openMenu);
  const isSearchOpen = useSelector((state: AppState) => state.searchDropdownVisibleSlice.isVisible);
  const isDrawerOpen = openMenu !== "none";

  // Handle z-index based on drawer state (only needed on mobile)
  useEffect(() => {
    if (!containerRef.current) return;

    if (isDrawerOpen) {
      containerRef.current.setAttribute("drawer-open", "true");
    } else {
      containerRef.current.removeAttribute("drawer-open");
    }

    // Lower z-index when search is open on homepage
    if (isHomepage && isSearchOpen) {
      containerRef.current.setAttribute("homepage-search-open", "true");
    } else {
      containerRef.current.removeAttribute("homepage-search-open");
    }
  }, [isDrawerOpen, isHomepage, isSearchOpen]);

  return (
    <div className={styles["announcementBarContainer"]} ref={containerRef}>
      {children}
    </div>
  );
}
